import classnames from 'classnames/bind';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import checkmark from '@coursera/assets/icons/check-mark.svg';
import wrongIcon from '@coursera/assets/icons/wrong-icon.svg';
import { CircleModal, LinearNavigation, RadioButtonGroup } from '@coursera/components';

import styles from './multiple-choice.module.scss';

const cx = classnames.bind(styles);

function MultipleChoice({
  questionsTitle,
  answersTitle,
  categories,
  conversationStyle,
  questions,
  currentProgress,
  setProgress,
  isCompleted,
  altView,
  width,
  questionState,
  maxHeight,
  noStyle,
  scroll,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(questions[0]);
  const [answer, setAnswer] = useState();
  const [answers, setAnswers] = useState({});
  const [incorrectAnswers, setIncorrectAnswers] = useState([]);
  const ref = useRef();
  const progressPerAnswer = 1 / questions.length + 0.0001;

  const currentIndex = useMemo(
    () => currentQuestion && questions.findIndex((cat) => cat.id === currentQuestion.id),
    [currentQuestion, questions],
  );

  const isQuestionStateIncomplete = useMemo(
    () => questionState && questionState[currentQuestion.id] === false,
    [currentQuestion, questionState],
  );

  const isAnswerCorrect = useMemo(
    () => !currentQuestion.answer || currentQuestion.answer.includes(answer),
    [answer, currentQuestion],
  );

  const getOptions = (options) =>
    options.map((opt) => ({
      id: opt,
      label: categories.find((cat) => cat.id === opt).label,
    }));

  const onSelected = (value) => {
    if (incorrectAnswers.includes(value)) return;

    setAnswer(value);
    setAnswers({ ...answers, [currentQuestion.id]: value });

    if (currentQuestion.answer.includes(value)) {
      questions[currentIndex].completed = true;
      setCurrentQuestion({ ...currentQuestion, completed: true });
    } else if (!incorrectAnswers.includes(value)) {
      setIncorrectAnswers([...incorrectAnswers, value]);
    }

    if (value !== undefined) {
      setModalOpen(true);
    }
  };

  const prevQuestion = () => {
    if (currentIndex === 0) {
      return;
    }
    const question = questions[currentIndex - 1];
    setCurrentQuestion(question);
    setAnswer(answers[question.id]);
    setIncorrectAnswers([]);
  };

  const nextQuestion = () => {
    if (currentIndex === questions.length - 1 || !currentQuestion.completed) {
      return;
    }

    const question = questions[currentIndex + 1];
    setCurrentQuestion(question);
    setAnswer(answers[question.id]);
    setIncorrectAnswers([]);
  };

  const getModalCta = () => {
    const category = categories.find((cat) => cat.id === answer);

    if (_.isEmpty(category)) return '';

    return isAnswerCorrect ? category.correctFeedback.ctaText : category.incorrectFeedback.ctaText;
  };

  const getModalDescription = () => {
    const category = categories.find((cat) => cat.id === answer);

    if (_.isEmpty(category)) {
      return '';
    }

    const modalInfo = isAnswerCorrect ? category.correctFeedback : category.incorrectFeedback;

    return (
      <div>
        <h6>{modalInfo.title}</h6>
        <p>{modalInfo.description}</p>
      </div>
    );
  };

  const closeModal = () => {
    if (isAnswerCorrect) {
      setProgress(currentProgress + progressPerAnswer);
      nextQuestion();
    }
    setModalOpen(false);
  };

  const getQuestionCardMinHeight = (categories) => {
    return {
      minHeight: `${categories.length * 53}px`,
      maxHeight,
    };
  };

  const nextScroll = useCallback(() => {
    if (ref && ref.current) {
      const position = scroll === 'auto' ? ref.current.scrollHeight : 0;
      setTimeout(() => ref.current.scrollTo(0, position), 0);
    }
  }, [scroll]);

  useEffect(() => {
    if (currentIndex > 0) {
      nextScroll();
    }
  }, [currentIndex, nextScroll]);

  return (
    <>
      <div
        className={cx({
          container: true,
          alt: altView,
          clean: noStyle,
          small: width && width <= 800,
          'conversation-style': conversationStyle,
        })}
        style={width ? { maxWidth: width } : {}}
      >
        <section>
          <h3 className={styles.questionsTitle}>{questionsTitle}</h3>
          <section
            style={getQuestionCardMinHeight(currentQuestion.categories)}
            className={styles.questionCard}
            aria-live="polite"
          >
            {scroll !== 'none' ? (
              <div className={styles.scroller} ref={ref}>
                {currentQuestion.description}
              </div>
            ) : (
              currentQuestion.description
            )}
          </section>
          {questions.length > 1 && (
            <section
              className={cx({
                complete: isCompleted,
                nav: true,
                incomplete: isQuestionStateIncomplete,
              })}
            >
              <LinearNavigation
                item={currentQuestion}
                allItems={questions}
                isStrict={true}
                onBack={prevQuestion}
                onNext={nextQuestion}
              />
            </section>
          )}
        </section>
        <section className={styles.answers}>
          <h3 className={styles.answersTitle}>{answersTitle}</h3>
          <RadioButtonGroup
            options={getOptions(currentQuestion.categories)}
            incorrect={incorrectAnswers}
            selection={answer}
            selected={(value) => onSelected(value)}
            disabled={isAnswerCorrect || isQuestionStateIncomplete}
          />
        </section>
      </div>

      <CircleModal
        open={modalOpen}
        appElementId="root"
        ctaText={getModalCta()}
        closeModal={closeModal}
        icon={isAnswerCorrect ? checkmark : wrongIcon}
        requestClose={closeModal}
      >
        {getModalDescription()}
      </CircleModal>
    </>
  );
}

MultipleChoice.propTypes = {
  altView: PropTypes.bool,
  answersTitle: PropTypes.string,
  categories: PropTypes.array,
  conversationStyle: PropTypes.bool,
  currentProgress: PropTypes.number,
  isCompleted: PropTypes.bool,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  noStyle: PropTypes.bool,
  questionState: PropTypes.objectOf(PropTypes.bool),
  questions: PropTypes.array,
  questionsTitle: PropTypes.string,
  scroll: PropTypes.string,
  setProgress: PropTypes.func,
  width: PropTypes.number,
};

MultipleChoice.defaultProps = {
  scroll: 'none',
  isCompleted: false,
};

export default MultipleChoice;
